import { useRef } from "react";
import type { KeyboardEvent } from "react";

/** The underline style of a tab or sub-navigation link, active or not. Shared
 *  with Settings' route links so the two kinds of tabs look the same. */
export function tabClass(active: boolean): string {
  return [
    "border-b-2 px-1 pb-2 text-sm font-medium transition-colors focus-visible:focus-ring",
    active ? "border-brand-600 text-[var(--text)]" : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]",
  ].join(" ");
}

/** The ids a tab and its panel share, so the page's panels can point back. */
export const tabIds = (prefix: string, key: string) => ({ tab: `${prefix}-tab-${key}`, panel: `${prefix}-panel-${key}` });

/**
 * An ARIA tablist: one tab stop (the selected tab), arrows / Home / End to
 * move, and selection follows focus. The page renders each panel itself with
 * role="tabpanel" and the matching tabIds, showing only the selected one.
 */
export function Tabs<T extends string>({ label, prefix, tabs, value, onChange }: { label: string; prefix: string; tabs: { key: T; label: string }[]; value: T; onChange: (key: T) => void }) {
  const list = useRef<HTMLDivElement>(null);
  const select = (i: number) => {
    const next = tabs[(i + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.key);
    list.current?.querySelector<HTMLButtonElement>(`#${tabIds(prefix, next.key).tab}`)?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex((t) => t.key === value);
    const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    select(to);
  };

  return (
    <div ref={list} role="tablist" aria-label={label} onKeyDown={onKeyDown} className="mb-6 flex gap-6 border-b border-[var(--border)]">
      {tabs.map((t) => {
        const ids = tabIds(prefix, t.key);
        const active = t.key === value;
        return (
          <button key={t.key} id={ids.tab} type="button" role="tab" aria-selected={active} aria-controls={ids.panel} tabIndex={active ? 0 : -1} onClick={() => onChange(t.key)} className={tabClass(active)}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
